import {
    BrowserAuthError,
    BrowserAuthErrorCodes,
    InteractionRequiredAuthError,
    type AccountInfo,
    type IPublicClientApplication,
} from '@azure/msal-browser';

export type LoginRequest = { scopes: string[] };

/**
 * Decides whether a failed silent request can only be resolved by sending the user through an
 * interactive sign-in.
 *
 * Besides the server telling us so, a timed-out hidden iframe counts as well: browsers that
 * partition third-party storage never hand the Entra session cookie to the iframe, so the renewal
 * can only fail there - just without a verdict we could read.
 */
export const requiresInteraction = (error: unknown): boolean =>
    error instanceof InteractionRequiredAuthError ||
    (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout);

const isInteractionInProgress = (error: unknown): boolean =>
    error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.interactionInProgress;

/**
 * Returns an access token for the active account, falling back to an interactive redirect when the
 * cached credentials have run out. Rejects with the original failure so callers still see why the
 * silent attempt gave up.
 *
 * @ai-generated 2026-09-21T15:07:37+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
export async function acquireToken(pca: IPublicClientApplication, loginRequest: LoginRequest): Promise<string> {
    const account = pca.getActiveAccount();
    if (account === null) {
        return '';
    }

    await Promise.resolve(); // Bugfix from https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5796
    try {
        const response = await pca.acquireTokenSilent({ ...loginRequest, account: account });
        return response.accessToken;
    } catch (error) {
        if (!requiresInteraction(error)) {
            throw error;
        }

        await startInteractiveLogin(pca, loginRequest, account, error);
        throw error;
    }
}

/**
 * Sends the user to the sign-in page, tolerating a redirect that a parallel token request already
 * started. Several requests can fail silently at once, and only the first of them gets to navigate.
 *
 * @ai-generated 2026-09-21T15:07:37+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
export async function startInteractiveLogin(
    pca: IPublicClientApplication,
    loginRequest: LoginRequest,
    account: AccountInfo | undefined,
    silentError: unknown,
): Promise<void> {
    try {
        await pca.acquireTokenRedirect({ ...loginRequest, account: account });
    } catch (redirectError) {
        if (!isInteractionInProgress(redirectError)) {
            throw redirectError;
        }

        console.debug('Interactive login already in progress, skipping redirect for', silentError);
    }
}
